import React, { useState } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';

const COMMON_TIMEZONES = [
  'UTC',
  'Asia/Kuala_Lumpur',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Australia/Sydney',
];

function SequenceManagerTab({
  sequences,
  mailboxes,
  loading,
  onRefresh,
  canManage,
  initialTemplateToApply,
  onClearInitialTemplate,
}) {
  const [activeModal, setActiveModal] = useState(null); // 'create' | 'edit_steps' | 'enroll'
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceDetails, setSequenceDetails] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form states
  const [newSeqName, setNewSeqName] = useState('');
  const [stepsForm, setStepsForm] = useState([
    {
      stepOrder: 1,
      templateId: null,
      templateName: '',
      templateVersion: 1,
      subject: '',
      body: '',
      delayValue: 0,
      delayUnit: 'days',
      sendAtLocal: '09:00',
    },
  ]);
  const [enrollForm, setEnrollForm] = useState({
    contactEmail: '',
    leadId: '',
    timezone: 'Asia/Kuala_Lumpur',
  });

  // Load approved templates for sequence step selection
  React.useEffect(() => {
    let mounted = true;
    async function loadTemplates() {
      try {
        const res = await emailOutreachApi.listTemplates({ status: 'approved' });
        if (mounted && res?.success && Array.isArray(res.data)) {
          setAvailableTemplates(res.data);
        }
      } catch (err) {
        console.warn('Failed to load approved templates for sequence builder:', err);
      }
    }
    loadTemplates();
    return () => {
      mounted = false;
    };
  }, []);

  // Handle direct prefill from Templates tab
  React.useEffect(() => {
    if (initialTemplateToApply) {
      setNewSeqName(initialTemplateToApply.name ? `${initialTemplateToApply.name} Outreach` : '');
      setStepsForm([
        {
          stepOrder: 1,
          templateId: initialTemplateToApply.id || null,
          templateName: initialTemplateToApply.name || '',
          templateVersion: initialTemplateToApply.version || 1,
          subject: initialTemplateToApply.subject_template || '',
          body: initialTemplateToApply.body_template || '',
          delayValue: 0,
          delayUnit: 'days',
          sendAtLocal: '09:00',
        },
      ]);
      setError(null);
      setActiveModal('create');
      if (onClearInitialTemplate) onClearInitialTemplate();
    }
  }, [initialTemplateToApply, onClearInitialTemplate]);

  const openCreateModal = () => {
    setNewSeqName('');
    setStepsForm([
      {
        stepOrder: 1,
        templateId: null,
        templateName: '',
        templateVersion: 1,
        subject: '',
        body: '',
        delayValue: 0,
        delayUnit: 'days',
        sendAtLocal: '09:00',
      },
    ]);
    setError(null);
    setActiveModal('create');
  };

  const openEditStepsModal = async (seq) => {
    try {
      setError(null);
      setSelectedSequence(seq);
      const res = await emailOutreachApi.getSequence(seq.id);
      if (res.success && res.data) {
        setSequenceDetails(res.data);
        const steps = (res.data.steps || []).map((s, i) => ({
          stepOrder: s.step_order || i + 1,
          templateId: s.template_id || null,
          templateName: s.template_name || '',
          templateVersion: s.template_version || 1,
          subject: s.subject || '',
          body: s.body || '',
          delayValue: s.delay_value ?? 0,
          delayUnit: s.delay_unit || 'days',
          sendAtLocal: s.send_at_local || '09:00',
        }));
        setStepsForm(
          steps.length > 0
            ? steps
            : [
                {
                  stepOrder: 1,
                  templateId: null,
                  templateName: '',
                  templateVersion: 1,
                  subject: '',
                  body: '',
                  delayValue: 0,
                  delayUnit: 'days',
                  sendAtLocal: '09:00',
                },
              ]
        );
        setActiveModal('edit_steps');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load sequence details');
    }
  };

  const openEnrollModal = (seq) => {
    setSelectedSequence(seq);
    setEnrollForm({ contactEmail: '', leadId: '', timezone: 'Asia/Kuala_Lumpur' });
    setError(null);
    setActiveModal('enroll');
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedSequence(null);
    setError(null);
  };

  const handleAddStep = () => {
    setStepsForm((prev) => [
      ...prev,
      {
        stepOrder: prev.length + 1,
        templateId: null,
        templateName: '',
        templateVersion: 1,
        subject: '',
        body: '',
        delayValue: 2,
        delayUnit: 'days',
        sendAtLocal: '09:00',
      },
    ]);
  };

  const handleRemoveStep = (index) => {
    if (stepsForm.length <= 1) return;
    setStepsForm((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStepChange = (index, field, value) => {
    setStepsForm((prev) =>
      prev.map((step, i) => (i === index ? { ...step, [field]: value } : step))
    );
  };

  const handleApplyTemplateToStep = (index, templateId) => {
    if (!templateId) {
      setStepsForm((prev) =>
        prev.map((step, i) =>
          i === index ? { ...step, templateId: null, templateName: '', templateVersion: 1 } : step
        )
      );
      return;
    }
    const tmpl = availableTemplates.find((t) => t.id === templateId);
    if (tmpl) {
      setStepsForm((prev) =>
        prev.map((step, i) =>
          i === index
            ? {
                ...step,
                templateId: tmpl.id,
                templateName: tmpl.name,
                templateVersion: tmpl.version || 1,
                subject: tmpl.subject_template || step.subject,
                body: tmpl.body_template || step.body,
              }
            : step
        )
      );
    }
  };

  const handleCreateSequence = async (e) => {
    e.preventDefault();
    if (!newSeqName.trim()) {
      setError('Sequence name is required');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await emailOutreachApi.createSequence({
        name: newSeqName.trim(),
        steps: stepsForm,
      });
      if (res.success) {
        closeModal();
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create sequence');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSteps = async (e) => {
    e.preventDefault();
    if (!selectedSequence) return;
    try {
      setSubmitting(true);
      setError(null);
      const res = await emailOutreachApi.updateSteps(selectedSequence.id, stepsForm);
      if (res.success) {
        closeModal();
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update steps');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (seq) => {
    const nextStatus = seq.status === 'active' ? 'draft' : 'active';
    try {
      setError(null);
      await emailOutreachApi.setSequenceStatus(seq.id, nextStatus);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update status');
    }
  };

  const handleAssignMailbox = async (seqId, connectionId) => {
    try {
      setError(null);
      await emailOutreachApi.assignMailbox(seqId, connectionId || null);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to assign mailbox');
    }
  };

  const handleEnrollContact = async (e) => {
    e.preventDefault();
    if (!selectedSequence) return;
    if (!enrollForm.contactEmail.trim()) {
      setError('Contact email is required');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await emailOutreachApi.enrollContact(selectedSequence.id, {
        contactEmail: enrollForm.contactEmail.trim(),
        leadId: enrollForm.leadId.trim() || null,
        timezone: enrollForm.timezone,
      });
      if (res.success) {
        alert(`Contact enrolled successfully into "${selectedSequence.name}".`);
        closeModal();
        onRefresh();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to enroll contact');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="eo-tab-content">
      <div className="eo-tab-header">
        <div>
          <h3 className="eo-section-title">Automated Email Sequences</h3>
          <p className="eo-section-desc">
            Configure multi-step outreach schedules with timezone-aware send windows. Sequences
            automatically halt if the recipient replies, books a meeting, submits a form, or
            unsubscribes.
          </p>
        </div>
        {canManage && (
          <div className="eo-tab-actions">
            <button className="eo-btn eo-btn-primary" onClick={openCreateModal}>
              <i className="fas fa-plus"></i> New Sequence
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="eo-alert eo-alert-error">
          <i className="fas fa-exclamation-triangle"></i>
          <span>{error}</span>
          <button className="eo-alert-close" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {loading ? (
        <div className="eo-loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading sequences...</span>
        </div>
      ) : sequences.length === 0 ? (
        <div className="eo-empty-state">
          <div className="eo-empty-icon">
            <i className="fas fa-route"></i>
          </div>
          <h4>No Sequences Created</h4>
          <p>
            Create your first multi-step sequence with personalized steps and automated stop
            conditions.
          </p>
          {canManage && (
            <button className="eo-btn eo-btn-primary" onClick={openCreateModal}>
              <i className="fas fa-plus"></i> Create Sequence
            </button>
          )}
        </div>
      ) : (
        <div className="eo-sequences-table-wrapper">
          <table className="eo-table">
            <thead>
              <tr>
                <th>Sequence Name</th>
                <th>Sender Mailbox</th>
                <th>Steps</th>
                <th>Active Leads</th>
                <th>Status</th>
                {canManage && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sequences.map((seq) => {
                const isActive = seq.status === 'active';
                return (
                  <tr key={seq.id}>
                    <td>
                      <div className="eo-seq-name-cell">
                        <strong>{seq.name}</strong>
                        <span className="eo-meta-text">
                          Created: {new Date(seq.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </td>
                    <td>
                      {canManage ? (
                        <select
                          className="eo-select-inline"
                          value={seq.connection_id || ''}
                          onChange={(e) => handleAssignMailbox(seq.id, e.target.value)}
                        >
                          <option value="">Shared Platform SMTP</option>
                          {mailboxes.map((mb) => (
                            <option key={mb.id} value={mb.id}>
                              {mb.account_email} ({mb.provider})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>{seq.mailbox_email || 'Shared Platform SMTP'}</span>
                      )}
                    </td>
                    <td>
                      <span className="eo-badge-steps">
                        <i className="fas fa-layer-group"></i> {seq.step_count || 0} step(s)
                      </span>
                    </td>
                    <td>
                      <div className="eo-enroll-stat-pills">
                        <span className="eo-stat-pill active" title="Active in progress">
                          {seq.active_enrollments || 0} active
                        </span>
                        <span
                          className="eo-stat-pill stopped"
                          title="Stopped (reply/meeting/opt-out)"
                        >
                          {seq.stopped_enrollments || 0} stopped
                        </span>
                        <span className="eo-stat-pill completed" title="Completed all steps">
                          {seq.completed_enrollments || 0} done
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`eo-status-pill ${isActive ? 'active' : 'draft'}`}>
                        {seq.status}
                      </span>
                    </td>
                    {canManage && (
                      <td style={{ textAlign: 'right' }}>
                        <div className="eo-btn-group">
                          <button
                            className="eo-btn eo-btn-sm eo-btn-secondary"
                            onClick={() => openEditStepsModal(seq)}
                            title="Configure steps"
                          >
                            <i className="fas fa-edit"></i> Edit Steps
                          </button>
                          <button
                            className={`eo-btn eo-btn-sm ${isActive ? 'eo-btn-warning' : 'eo-btn-success'}`}
                            onClick={() => handleToggleStatus(seq)}
                          >
                            <i className={isActive ? 'fas fa-pause' : 'fas fa-play'}></i>
                            {isActive ? ' Pause' : ' Activate'}
                          </button>
                          {isActive && (
                            <button
                              className="eo-btn eo-btn-sm eo-btn-primary"
                              onClick={() => openEnrollModal(seq)}
                              title="Enroll lead into sequence"
                            >
                              <i className="fas fa-user-plus"></i> Enroll
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- CREATE / EDIT STEPS MODAL --- */}
      {(activeModal === 'create' || activeModal === 'edit_steps') && (
        <div className="eo-modal-overlay">
          <div className="eo-modal-card eo-modal-lg">
            <div className="eo-modal-header">
              <h3>
                {activeModal === 'create'
                  ? 'Create New Sequence'
                  : `Edit Steps: ${selectedSequence?.name}`}
              </h3>
              <button className="eo-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={activeModal === 'create' ? handleCreateSequence : handleSaveSteps}>
              <div className="eo-modal-body">
                {activeModal === 'create' && (
                  <div className="eo-form-group">
                    <label className="eo-form-label">Sequence Name *</label>
                    <input
                      type="text"
                      className="eo-input"
                      value={newSeqName}
                      onChange={(e) => setNewSeqName(e.target.value)}
                      placeholder="e.g. Enterprise Q4 Outbound Follow-up"
                      required
                    />
                  </div>
                )}

                <div className="eo-steps-container">
                  <div className="eo-steps-header">
                    <h4>Sequence Steps (Chronological Order)</h4>
                    <button
                      type="button"
                      className="eo-btn eo-btn-sm eo-btn-secondary"
                      onClick={handleAddStep}
                    >
                      <i className="fas fa-plus"></i> Add Step
                    </button>
                  </div>

                  {stepsForm.map((step, idx) => (
                    <div key={idx} className="eo-step-box">
                      <div className="eo-step-box-header">
                        <span className="eo-step-number">Step {idx + 1}</span>
                        {stepsForm.length > 1 && (
                          <button
                            type="button"
                            className="eo-btn-icon-danger"
                            onClick={() => handleRemoveStep(idx)}
                            title="Remove step"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>

                      <div className="eo-step-timing-row">
                        <div className="eo-timing-field">
                          <label>Delay from previous:</label>
                          <div className="eo-input-group-compact">
                            <input
                              type="number"
                              min="0"
                              className="eo-input eo-input-sm"
                              style={{ width: '80px' }}
                              value={step.delayValue}
                              onChange={(e) =>
                                handleStepChange(
                                  idx,
                                  'delayValue',
                                  parseInt(e.target.value, 10) || 0
                                )
                              }
                            />
                            <select
                              className="eo-select eo-select-sm"
                              value={step.delayUnit}
                              onChange={(e) => handleStepChange(idx, 'delayUnit', e.target.value)}
                            >
                              <option value="minutes">Minutes</option>
                              <option value="hours">Hours</option>
                              <option value="days">Days</option>
                            </select>
                          </div>
                        </div>

                        <div className="eo-timing-field">
                          <label>Send at Local Time (Optional):</label>
                          <input
                            type="time"
                            className="eo-input eo-input-sm"
                            value={step.sendAtLocal || ''}
                            onChange={(e) =>
                              handleStepChange(idx, 'sendAtLocal', e.target.value || null)
                            }
                          />
                        </div>
                      </div>

                      {/* Template Governance Selector */}
                      <div className="eo-form-group eo-template-selector-box">
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '6px',
                          }}
                        >
                          <label className="eo-form-label" style={{ margin: 0, fontSize: '12px' }}>
                            <i className="fas fa-file-contract"></i> Approved Template (Optional)
                          </label>
                          {step.templateId && (
                            <span className="eo-badge-governed">
                              <i className="fas fa-shield-alt"></i> Governed (
                              {step.templateName || 'Template'} v{step.templateVersion || 1})
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <select
                            className="eo-select"
                            value={step.templateId || ''}
                            onChange={(e) => handleApplyTemplateToStep(idx, e.target.value)}
                          >
                            <option value="">-- Custom copy (No template linked) --</option>
                            {availableTemplates.map((t) => (
                              <option key={t.id} value={t.id}>
                                [{t.category.toUpperCase().replace(/_/g, ' ')}] {t.name} (v
                                {t.version})
                              </option>
                            ))}
                          </select>
                          {step.templateId && (
                            <button
                              type="button"
                              className="eo-btn eo-btn-sm eo-btn-secondary"
                              onClick={() => handleApplyTemplateToStep(idx, '')}
                              title="Unlink template from this step"
                            >
                              Unlink
                            </button>
                          )}
                        </div>
                        {step.templateId && (
                          <small style={{ color: '#059669', display: 'block', marginTop: '4px' }}>
                            <i className="fas fa-check-circle"></i> Subject and body populated from
                            approved template. Compliance footer will be enforced automatically.
                          </small>
                        )}
                      </div>

                      <div className="eo-form-group">
                        <label className="eo-form-label">Subject *</label>
                        <input
                          type="text"
                          className="eo-input"
                          value={step.subject}
                          onChange={(e) => handleStepChange(idx, 'subject', e.target.value)}
                          placeholder="Email subject line"
                          required
                        />
                      </div>

                      <div className="eo-form-group">
                        <label className="eo-form-label">Body Content *</label>
                        <textarea
                          className="eo-textarea"
                          rows="4"
                          value={step.body}
                          onChange={(e) => handleStepChange(idx, 'body', e.target.value)}
                          placeholder="Compose email body. Variables like {{firstName}} will be populated."
                          required
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="eo-modal-footer">
                <button type="button" className="eo-btn eo-btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="eo-btn eo-btn-primary" disabled={submitting}>
                  {submitting
                    ? 'Saving...'
                    : activeModal === 'create'
                      ? 'Create Sequence'
                      : 'Save Step Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- ENROLL CONTACT MODAL --- */}
      {activeModal === 'enroll' && (
        <div className="eo-modal-overlay">
          <div className="eo-modal-card">
            <div className="eo-modal-header">
              <h3>Enroll Contact in "{selectedSequence?.name}"</h3>
              <button className="eo-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleEnrollContact}>
              <div className="eo-modal-body">
                <div className="eo-form-group">
                  <label className="eo-form-label">Contact Email *</label>
                  <input
                    type="email"
                    className="eo-input"
                    value={enrollForm.contactEmail}
                    onChange={(e) => setEnrollForm((f) => ({ ...f, contactEmail: e.target.value }))}
                    placeholder="prospect@company.com"
                    required
                  />
                </div>

                <div className="eo-form-group">
                  <label className="eo-form-label">Recipient Local Timezone</label>
                  <select
                    className="eo-select"
                    value={enrollForm.timezone}
                    onChange={(e) => setEnrollForm((f) => ({ ...f, timezone: e.target.value }))}
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                  <span className="eo-help-text">
                    Frozen at enrollment. Step schedule times will calculate according to this local
                    wall clock.
                  </span>
                </div>
              </div>

              <div className="eo-modal-footer">
                <button type="button" className="eo-btn eo-btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="eo-btn eo-btn-primary" disabled={submitting}>
                  {submitting ? 'Enrolling...' : 'Start Enrollment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SequenceManagerTab;
